import { useCallback } from 'react';
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { generatePreview } from "@/utils/generatePreview";
import html2canvas from 'html2canvas';

interface UseFileActionsProps {
  editorRef: React.MutableRefObject<any>;
  onAuthRequired: () => void;
  isAuthenticated: boolean;
  onFloorPlanNameUpdate?: (name: string) => void;
}

export const useFileActions = ({ editorRef, onAuthRequired, isAuthenticated, onFloorPlanNameUpdate }: UseFileActionsProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSave = useCallback(async () => {
    if (!editorRef.current || !isAuthenticated || !user) {
      onAuthRequired();
      return;
    }

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const planId = urlParams.get('id');

      if (!planId) {
        throw new Error('Geen actief plan. Gebruik "Opslaan als" om een nieuw plan te maken.');
      }

      const nodes = editorRef.current.getNodes() || [];
      const edges = editorRef.current.getEdges() || [];
      const data = { nodes, edges };
      
      // Controleer toegang tot het plan
      const { data: existingPlan, error: fetchError } = await supabase
        .from('floor_plans')
        .select('*')
        .eq('id', planId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (fetchError || !existingPlan) {
        throw new Error('Geen toegang tot deze plattegrond');
      }

      // Update bestaand plan
      const { error: updateError } = await supabase
        .from('floor_plans')
        .update({
          content: data,
          updated_at: new Date().toISOString(),
          is_active: true
        })
        .eq('id', planId)
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      // Eerst alle andere plattegronden op niet-actief zetten
      await supabase
        .from('floor_plans')
        .update({ is_active: false })
        .eq('user_id', user.id)
        .neq('id', planId);

      // Genereer nieuwe preview
      try {
        await generatePreview(planId);
      } catch (previewError) {
        console.error('Error generating preview:', previewError);
        // We laten de save actie niet falen als alleen de preview generatie mislukt
      }

      toast({
        title: "Plattegrond opgeslagen",
        description: "Je wijzigingen zijn succesvol opgeslagen",
      });
    } catch (error: any) {
      console.error('Save error:', error);
      toast({
        title: "Fout bij opslaan",
        description: error.message || "Er is een fout opgetreden bij het opslaan",
        variant: "destructive",
      });
    }
  }, [editorRef, isAuthenticated, user, toast, onAuthRequired]);

  const saveToDatabase = useCallback(async (data: any, name: string | null = null, previewImage: string | null = null) => {
    if (!user) throw new Error('Gebruiker niet ingelogd');

    try {
      console.log('Saving new floor plan to database:', { name, previewImage });

      // Maak altijd een nieuwe plattegrond aan
      const insertData = {
        content: data,
        name: name || 'Nieuwe plattegrond',
        user_id: user.id,
        preview_image: previewImage,
        is_active: true,
        is_public: true
      };

      // Eerst alle andere plattegronden op niet-actief zetten
      await supabase
        .from('floor_plans')
        .update({ is_active: false })
        .eq('user_id', user.id);

      // Maak nieuwe plattegrond aan
      const { data: newPlan, error: insertError } = await supabase
        .from('floor_plans')
        .insert(insertData)
        .select('id')
        .single();

      if (insertError) {
        console.error('Error creating new floor plan:', insertError);
        throw insertError;
      }

      console.log('Successfully created new floor plan:', newPlan);
      return newPlan;
    } catch (error) {
      console.error('Error in saveToDatabase:', error);
      throw error;
    }
  }, [user]);

  const handleSaveAs = useCallback(async (name: string) => {
    if (!editorRef.current || !isAuthenticated || !user) {
      onAuthRequired();
      return;
    }

    if (!name.trim()) {
      toast({
        title: "Fout bij opslaan",
        description: "Geef een naam op voor de plattegrond",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('Starting SaveAs operation with name:', name);
      
      const nodes = editorRef.current.getNodes() || [];
      const edges = editorRef.current.getEdges() || [];
      const data = { nodes, edges };

      const container = document.getElementById('floor-plan-container');
      if (!container) return;

      const canvas = await html2canvas(container, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
      });

      const previewImage = canvas.toDataURL('image/png');
      console.log('Generated preview image:', previewImage ? 'success' : 'failed');

      const newPlan = await saveToDatabase(data, name, previewImage);
      console.log('SaveAs operation completed successfully:', newPlan);

      // Update the current floor plan name
      if (onFloorPlanNameUpdate) {
        onFloorPlanNameUpdate(name);
      }

      toast({
        title: "Plattegrond opgeslagen",
        description: "Je plattegrond is succesvol opgeslagen als '" + name + "'",
      });
      
      navigate(`/?id=${newPlan.id}`);
    } catch (error: any) {
      console.error('SaveAs error:', error);
      toast({
        title: "Fout bij opslaan",
        description: error.message || "Er is een fout opgetreden bij het opslaan",
        variant: "destructive",
      });
    }
  }, [editorRef, isAuthenticated, user, saveToDatabase, toast, navigate, onAuthRequired, onFloorPlanNameUpdate]);

  const handleLoad = useCallback(async (planId: string) => {
    if (!isAuthenticated || !user) {
      onAuthRequired();
      return;
    }

    try {
      const { data: plan, error } = await supabase
        .from('floor_plans')
        .select('content, name')
        .eq('id', planId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      if (!plan) {
        throw new Error('Plattegrond niet gevonden');
      }

      const content = plan.content as any;
      if (!content || !Array.isArray(content.nodes) || !Array.isArray(content.edges)) {
        throw new Error('Ongeldig plattegrond formaat');
      }

      editorRef.current?.resetState(content.nodes, content.edges);
      
      // Update the current floor plan name
      if (onFloorPlanNameUpdate && plan.name) {
        onFloorPlanNameUpdate(plan.name);
      }
      
      // Update URL met het nieuwe plan ID
      navigate(`/?id=${planId}`);

      toast({
        title: "Plattegrond geladen",
        description: "De plattegrond is succesvol geladen",
      });
    } catch (error: any) {
      console.error('Load error:', error);
      toast({
        title: "Fout bij laden",
        description: error.message || "Er is een fout opgetreden bij het laden",
        variant: "destructive",
      });
    }
  }, [isAuthenticated, user, editorRef, navigate, toast, onAuthRequired, onFloorPlanNameUpdate]);

  return {
    handleSave,
    handleSaveAs,
    handleLoad,
  };
};
